import React from 'react';
import { ClientContext } from '~/services';
import moment from 'moment';
import { ReferralType, enumValue, formatAddress } from '~/util';
import { ProviderEntityEnum, ProviderType, getProviderInfo } from '~/util/providerUtils';

interface ProviderPrintProps {
    referralStatus: 'confirmed' | 'unconfirmed';
    patient: PatientType;
    provider: ProviderType;
    reason: string;
    notes: string;
    isDisabled?: boolean;
    sentReferral?: ReferralType;
    patientEmail?: string;
    location?: undefined | LocationType;
    btnClass?: string;
    btnText?: string;
}

export function ProviderPrint({
    referralStatus,
    reason,
    notes,
    provider,
    patient,
    isDisabled = false,
    sentReferral,
    patientEmail = '',
    location,
    btnClass = 'btn-primary',
    btnText = 'Patient Printout',
}: ProviderPrintProps) {
    const { reportYear } = React.useContext(ClientContext);
    const { providerName, providerIsContact } = getProviderInfo(provider);
    function printProvider() {
        const params = new URLSearchParams();
        params.append('referralStatus', referralStatus);
        params.append('reason', reason);
        params.append('notes', notes);
        params.append('printed', moment().format('M/DD/Y h:mma'));
        params.append(
            'sentDate',
            sentReferral ? moment(sentReferral.sent_on).format('M/DD/Y h:mma') : ''
        );
        params.append('providerName', providerName);
        params.append('providerClinic', `${provider?.orgName || provider?.practiceName || ''}`);
        params.append('referralId', sentReferral?.referral_message.base36Id || '');
        params.append('patientEmail', patientEmail);
        let providerPhone: undefined | PhoneType;
        if (providerIsContact) {
            providerPhone = provider.phones?.find(
                (phone) => phone.type === enumValue(['core_app', 'phone_types', 'Practice'])
            );
        } else {
            const locationPhone = location?.phones.find(
                (phone) => phone.type === enumValue(['core_app', 'phone_types', 'Practice'])
            );
            const organizationPhones =
                provider.entityType === ProviderEntityEnum.ORG
                    ? provider.phones
                    : provider.organization?.phones;
            const organizationPhone = organizationPhones?.find(
                (phone) => phone.type === enumValue(['core_app', 'phone_types', 'Main'])
            );
            providerPhone = locationPhone || organizationPhone;
        }
        params.append(
            'providerPhone',
            providerPhone
                ? `${providerPhone.number}${
                      providerPhone.extension ? ` Ext. ${providerPhone.extension}` : ''
                  }`
                : ''
        );

        params.append(
            'providerLocation',
            location && location.addresses.length > 0
                ? formatAddress(location.addresses[0]).join('\n')
                : ''
        );
        const pdfWindow = window.open(
            `/api/patients/${patient.id}/${reportYear}/referralPdf?${params}`
        );
        pdfWindow?.print();
    }
    return (
        <button
            type="button"
            className={`btn ${btnClass}`}
            disabled={isDisabled}
            onClick={printProvider}
        >
            {btnText}
        </button>
    );
}
